import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Laptop, Loader2 } from 'lucide-react';
import type { SignupWorkspace } from '@mailpilot/shared';
import { ApiError, api, errorMessage } from '../../lib/api';
import { bridge, deviceName } from '../../lib/desktop';
import { firebaseProviders, PROVIDER_LABEL, signInWithFirebase, useAuthConfig, type FirebaseProviderId } from '../../lib/firebase';
import { Button, InlineError } from '../ui';

function ProviderIcon({ id }: { id: FirebaseProviderId }) {
  switch (id) {
    case 'google':
      return (
        <svg viewBox="0 0 48 48" className="h-[18px] w-[18px]" aria-hidden>
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
        </svg>
      );
    case 'microsoft':
      return (
        <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden>
          <path fill="#F25022" d="M1 1h9v9H1z" />
          <path fill="#7FBA00" d="M11 1h9v9h-9z" />
          <path fill="#00A4EF" d="M1 11h9v9H1z" />
          <path fill="#FFB900" d="M11 11h9v9h-9z" />
        </svg>
      );
    case 'apple':
      return (
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden>
          <path fill="currentColor" d="M16.4 12.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4.1zM13.9 5c.7-.9 1.2-2 1-3.2-1 .1-2.3.7-3 1.6-.7.8-1.3 2-1.1 3.1 1.2.1 2.3-.6 3.1-1.5z" />
        </svg>
      );
    case 'github':
      return (
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden>
          <path fill="currentColor" d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.6v-2c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0C17.3 4.7 18.3 5 18.3 5c.7 1.6.3 2.8.1 3.1.8.8 1.2 1.9 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z" />
        </svg>
      );
  }
}

/** Desktop app: sign in by approving the device in a browser where you're signed in (works with any sign-in method). */
function BrowserSignIn({ onToken, providers, googleClientId, workspace }: { onToken: (token: string) => Promise<void>; providers: FirebaseProviderId[]; googleClientId: string | null; workspace?: SignupWorkspace }) {
  const [googleWaiting, setGoogleWaiting] = useState(false);
  const desktop = bridge();
  // Direct Google sign-in: Google's own page in the system browser, then straight back here.
  const startGoogle = async () => {
    if (!desktop?.googleSignIn || !googleClientId) return;
    setError(null);
    setGoogleWaiting(true);
    try {
      const r = await desktop.googleSignIn(googleClientId);
      if (!r) return; // cancelled or closed
      const res = await api('POST /api/auth/google/desktop', {
        body: { ...r, deviceName: await deviceName(), deviceKind: 'desktop', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, workspace },
      });
      await onToken(res.token);
    } catch (e) {
      setError(e);
    } finally {
      setGoogleWaiting(false);
    }
  };
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const cancelled = useRef(false);
  useEffect(() => () => void (cancelled.current = true), []);

  const start = async () => {
    setError(null);
    setBusy(true);
    cancelled.current = false;
    try {
      const r = await api('POST /api/auth/device/start', { body: { deviceName: await deviceName() } });
      setCode(r.userCode);
      window.open(r.verifyUrl, '_blank'); // the desktop shell opens this in the system browser
      const deadline = Date.now() + r.expiresIn * 1000;
      while (!cancelled.current && Date.now() < deadline) {
        await new Promise((ok) => setTimeout(ok, r.interval * 1000));
        if (cancelled.current) return;
        const p = await api('POST /api/auth/device/poll', { body: { pollToken: r.pollToken } });
        if (p.status === 'approved' && p.token) {
          await onToken(p.token);
          return;
        }
      }
      if (!cancelled.current) throw new Error('The request expired. Start again.');
    } catch (e) {
      setError(e instanceof ApiError && e.status === 410 ? new Error('The request expired. Start again.') : e);
      setCode(null);
    } finally {
      setBusy(false);
    }
  };

  if (googleWaiting) {
    return (
      <div className="space-y-3 rounded-2xl border border-primary/30 bg-primary-soft/50 p-4 text-center">
        <p className="flex items-center justify-center gap-2 text-[0.8125rem] font-medium text-ink">
          <Loader2 className="h-4 w-4 animate-spin" /> Finish signing in with Google in your browser…
        </p>
        <p className="text-xs text-muted">MailPilot continues automatically when you're done.</p>
        <Button size="sm" onClick={() => void desktop?.cancelGoogleSignIn?.()}>
          Cancel
        </Button>
      </div>
    );
  }
  if (code) {
    return (
      <div className="space-y-3 rounded-2xl border border-primary/30 bg-primary-soft/50 p-4 text-center">
        <p className="text-[0.8125rem] text-ink">Finish signing in in your browser. Make sure it shows this code:</p>
        <p className="font-mono text-2xl font-semibold tracking-[0.2em] text-primary-ink">{code}</p>
        <p className="flex items-center justify-center gap-2 text-xs text-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Waiting for approval…
        </p>
        <Button
          size="sm"
          onClick={() => {
            cancelled.current = true;
            setCode(null);
          }}
        >
          Cancel
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {(providers.length ? providers : (['google'] as FirebaseProviderId[])).map((p) => (
        <Button key={p} size="lg" block className="hover-lift" icon={<ProviderIcon id={p} />} iconRight={<ExternalLink className="h-4 w-4 opacity-50" />} loading={busy} onClick={() => void (p === 'google' && googleClientId && desktop?.googleSignIn ? startGoogle() : start())}>
          Continue with {PROVIDER_LABEL[p]}
        </Button>
      ))}
      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted">
        <Laptop className="h-3.5 w-3.5" /> Opens your browser to sign in, then brings you back here.
      </p>
      <InlineError error={error} />
    </div>
  );
}

/**
 * "Continue with Google / Microsoft / …" (Firebase). Renders nothing when Firebase isn't configured.
 * `blocked` explains why sign-up can't continue yet (e.g. organization name missing).
 */
export function SocialSignIn({
  workspace,
  blocked,
  onSignedIn,
  verb = 'Continue',
}: {
  workspace?: SignupWorkspace;
  blocked?: string | null;
  onSignedIn: (res: { token: string }) => Promise<void>;
  verb?: string;
}) {
  const cfg = useAuthConfig();
  const [busy, setBusy] = useState<FirebaseProviderId | null>(null);
  const [error, setError] = useState<unknown>(null);
  const fb = cfg.data?.firebase;
  if (!fb?.enabled || !fb.web) return null;
  const providers = firebaseProviders(fb.providers);

  const content = bridge() ? (
    <BrowserSignIn providers={providers} googleClientId={cfg.data?.googleDesktop?.clientId ?? null} workspace={workspace} onToken={(token) => onSignedIn({ token })} />
  ) : (
    <div className="space-y-2">
      {providers.map((p) => (
        <Button
          key={p}
          size="lg"
          block
          className="hover-lift"
          icon={busy === p ? <Loader2 className="h-4 w-4 animate-spin" /> : <ProviderIcon id={p} />}
          disabled={!!busy || !!blocked}
          title={blocked ?? undefined}
          onClick={async () => {
            setError(null);
            setBusy(p);
            try {
              const res = await signInWithFirebase(fb.web!, p, workspace);
              if (res) await onSignedIn(res);
            } catch (e) {
              setError(e);
            } finally {
              setBusy(null);
            }
          }}
        >
          {verb} with {PROVIDER_LABEL[p]}
        </Button>
      ))}
      {blocked && <p className="text-center text-xs text-muted">{blocked}</p>}
      {error ? <p className="text-center text-xs text-danger-ink" role="alert">{errorMessage(error)}</p> : null}
    </div>
  );
  return (
    <div className="space-y-4">
      {content}
      <div className="flex items-center gap-3 text-xs text-subtle" aria-hidden>
        <span className="h-px flex-1 bg-line" /> or use your email <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}
